import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { apiClient } from "@/lib/api";
import { BookingWindow, BookingWindowStatus } from "@/types/booking-window";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Eye, Pencil, Trash2, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useDebounce } from "@/hooks/use-debounce";

export default function BookingWindowsListPage() {
  const [bookingWindows, setBookingWindows] = useState<BookingWindow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Pagination & Filtering State
  const page = Number(searchParams.get("page")) || 1;
  const limit = Number(searchParams.get("limit")) || 10;
  const initialSearch = searchParams.get("search") || "";
  const initialStatus = searchParams.get("status") || "all";

  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [statusFilter, setStatusFilter] = useState<string>(initialStatus);
  const [total, setTotal] = useState(0);

  const debouncedSearch = useDebounce(searchTerm, 500);

  // Helper to update URL params
  const updateParams = (newPage: number, newLimit: number, search: string, status: string) => {
    const params: Record<string, string> = {
      page: String(newPage),
      limit: String(newLimit),
    };
    if (search) params.search = search;
    if (status !== "all") params.status = status;
    setSearchParams(params);
  };

  useEffect(() => {
    if (debouncedSearch !== initialSearch) {
        updateParams(1, limit, debouncedSearch, statusFilter);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (statusFilter !== initialStatus) {
        updateParams(1, limit, debouncedSearch, statusFilter);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchBookingWindows();
  }, [page, limit, debouncedSearch, statusFilter]);

  const fetchBookingWindows = async () => {
    setIsLoading(true);
    try {
      // Calculate skip based on page and limit
      const skip = (page - 1) * limit;
      const data = await apiClient.getAdminBookingWindows(statusFilter, debouncedSearch, skip, limit);
      
      setBookingWindows(data.items);
      setTotal(data.total);
    } catch (error) {
      console.error("Error fetching booking windows:", error);
      toast({
        title: "Error",
        description: "Failed to load booking windows",
        variant: "destructive",
      });
      setBookingWindows([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setIsDeleting(id);
      await apiClient.deleteBookingWindow(id);
      
      setBookingWindows((prev) => prev.filter((window) => window.id !== id));
      setTotal((prev) => prev - 1);
      
      toast({
        title: "Success",
        description: "Booking window and associated data deleted successfully",
      });
    } catch (error) {
      console.error("Error deleting booking window:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete booking window",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const getStatusColor = (status: BookingWindowStatus) => {
    switch (status) {
      case BookingWindowStatus.OPEN:
        return "bg-green-500 hover:bg-green-600 text-white border-none";
      case BookingWindowStatus.CLOSED:
        return "bg-red-500 hover:bg-red-600 text-white border-none";
      case BookingWindowStatus.DRAFT:
        return "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300";
      case BookingWindowStatus.PACKAGING:
        return "bg-blue-500 hover:bg-blue-600 text-white border-none";
      case BookingWindowStatus.PRESENTATION:
        return "bg-purple-500 hover:bg-purple-600 text-white border-none";
      case BookingWindowStatus.ARCHIVED:
        return "bg-gray-400 hover:bg-gray-500 text-white border-none";
      default:
        return "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300";
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] space-y-6 font-ibm overflow-hidden">
      {/* Header & Filters Container */}
      <div className="flex-none space-y-6 pb-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
             <div>
              <h1 className="text-3xl font-semibold text-foreground tracking-tight font-cabinet">
                Booking Windows
              </h1>
              <p className="text-muted-foreground font-ibm text-sm mt-1 max-w-sm">
                Manage campaign booking windows and timelines.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
               <Badge variant="outline" className="text-muted-foreground h-9 px-4 flex items-center justify-center font-ibm bg-card/60 backdrop-blur-sm">
                  Total: {total}
              </Badge>
              <Button asChild className="h-9 font-ibm">
                <Link to="/admin/booking-windows/create">
                  <Plus className="mr-2 h-4 w-4" />
                  Create Window
                </Link>
              </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                  <Search className="w-4 h-4" />
                  </div>
                  <input
                  type="text"
                  placeholder="Search booking windows..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 bg-card/60 backdrop-blur-sm border border-border rounded-lg text-sm placeholder:text-muted-foreground text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-sm font-ibm h-[38px]"
                  />
              </div>
              <div className="w-[180px]">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-[38px] bg-card/60 backdrop-blur-sm border-border text-xs font-medium font-ibm">
                    <SelectValue placeholder="Filter by Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value={BookingWindowStatus.DRAFT}>Draft</SelectItem>
                    <SelectItem value={BookingWindowStatus.OPEN}>Open</SelectItem>
                    <SelectItem value={BookingWindowStatus.CLOSED}>Closed</SelectItem>
                    <SelectItem value={BookingWindowStatus.PACKAGING}>Packaging</SelectItem>
                    <SelectItem value={BookingWindowStatus.PRESENTATION}>Presentation</SelectItem>
                    <SelectItem value={BookingWindowStatus.ARCHIVED}>Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Clear Filters Button */}
              {(statusFilter !== "all" || searchTerm !== "") && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setStatusFilter("all");
                    setSearchTerm("");
                  }}
                  className="h-[38px] text-xs font-ibm"
                >
                  Clear Filters
                </Button>
              )}
          </div>
        </div>
      </div>

      {/* Scrollable Table Container */}
      <div className="flex-1 min-h-0 rounded-xl border border-border bg-card shadow-sm overflow-hidden flex flex-col">
        <div className="flex-1 overflow-auto custom-scrollbar">
          {isLoading ? (
            <div className="flex justify-center items-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table className="min-w-[1000px]">
              <TableHeader className="sticky top-0 bg-card z-10 shadow-sm border-b border-border">
                <TableRow className="hover:bg-transparent border-b border-border">
                  <TableHead className="font-cabinet font-semibold text-foreground">Name</TableHead>
                  <TableHead className="font-cabinet font-semibold text-foreground">Status</TableHead>
                  <TableHead className="font-cabinet font-semibold text-foreground">Submission Period</TableHead>
                  <TableHead className="font-cabinet font-semibold text-foreground">Projects</TableHead>
                  <TableHead className="text-right font-cabinet font-semibold text-foreground">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookingWindows.map((window) => (
                  <TableRow key={window.id} className="hover:bg-secondary/50 border-b border-border transition-colors">
                    <TableCell className="font-medium">
                      <div className="flex flex-col">
                        <span className="font-semibold text-foreground">{window.name}</span>
                        <span className="text-xs text-muted-foreground truncate max-w-[200px] font-ibm">
                          /{window.slug}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getStatusColor(window.status)} capitalize font-ibm`}>
                        {window.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col text-sm font-ibm">
                        <span>
                          {format(new Date(window.submission_start_date), "MMM d, yyyy")}
                        </span>
                        <span className="text-muted-foreground text-xs">to</span>
                        <span>
                          {format(new Date(window.submission_end_date), "MMM d, yyyy")}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium font-ibm">{window.campaign_count || 0}</span>
                        <span className="text-xs text-muted-foreground font-ibm">Campaigns</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          asChild
                          title="View Details"
                          className="hover:bg-primary/10 hover:text-primary transition-colors"
                        >
                          <Link to={`/admin/booking-windows/${window.id}`}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Edit"
                          className="hover:bg-primary/10 hover:text-primary transition-colors"
                          onClick={() => navigate(`/admin/booking-windows/${window.id}/edit`)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Booking Window</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{window.name}"? This will also delete all associated campaigns and data. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(window.id)}
                                className="bg-destructive hover:bg-destructive/90"
                                disabled={isDeleting === window.id}
                              >
                                {isDeleting === window.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  "Delete"
                                )}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {bookingWindows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center text-muted-foreground font-ibm">
                      No booking windows found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Pagination Controls - Fixed at Bottom */}
      <div className="flex-none pt-4">
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium text-muted-foreground">Rows per page</p>
            <Select
              value={`${limit}`}
              onValueChange={(value) => {
                updateParams(1, Number(value), debouncedSearch, statusFilter);
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={limit} />
              </SelectTrigger>
              <SelectContent side="top">
                {[10, 20, 50, 100].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="flex w-[100px] items-center justify-center text-sm font-medium text-muted-foreground">
              Page {page} of {totalPages || 1}
            </div>
            <Button
              variant="outline"
              className="h-8 w-8 p-0 bg-card hover:bg-secondary border-border"
              onClick={() => updateParams(Math.max(1, page - 1), limit, debouncedSearch, statusFilter)}
              disabled={page === 1}
            >
              <span className="sr-only">Go to previous page</span>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0 bg-card hover:bg-secondary border-border"
              onClick={() => updateParams(Math.min(totalPages, page + 1), limit, debouncedSearch, statusFilter)}
              disabled={page === totalPages || totalPages === 0}
            >
              <span className="sr-only">Go to next page</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
